<template>
  <v-card class="payment-schedule">
    <v-card-title class="pb-2">
      <v-row>
        <v-col cols="12" class="py-0 flex justify-space-between flex-column flex-md-row">
          <span class="headline-2">
            <span v-if="offer.features.isSafeCredit2Percent">{{ $i18nFormatter($i18n.useScheduleModal.TITLE_BK_2, {installmentType: installmentType}) }}</span>
            <span v-else>{{ $i18nFormatter($i18n.useScheduleModal.NEW_TRANSLATION_TITLE, {installmentType: installmentType}) }}</span>
          </span>
          <v-btn outlined depressed class="mt-2 mt-md-0"
                 @click="scheduleStore.onShowAllItemsChange()">
            {{scheduleStore.isEveryScheduleRowVisible ? $i18n.useScheduleModal.HIDE_ROWS : $i18n.useScheduleModal.SHOW_ROWS}}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="pa-0">
      <v-row class="px-4 py-6 w-full">
        <v-col class="py-0">
          <template v-if="!store.userInput.hideOfferName">
            <v-row>
              <v-col cols="auto">
                <bank-logo :bank-symbol="offer.info.bank" class="mb-2" />
              </v-col>
              <v-col>
                <p class="body-1 font-weight-medium mb-1">{{ offer.info.bankName }}</p>
                <p>{{ offer.info.title }}</p>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <p class="font-weight-medium">{{offer.shortName}}</p>
          </template>
          <p class="mb-1">{{ $i18n.useScheduleModal.NET_GROSS_AMOUNT }}: <span class="font-weight-medium">{{ offer.totals.capital | numeralFormat }} {{ $env.currentCurrency() }}</span>
            <span v-if="offer.fxValues">({{ offer.fxValues.loanAmount | numeralFormat }} {{userInput.currency}})</span>
          </p>
          <p class="mb-1">{{ $i18n.useScheduleModal.LOAN_PERIOD }}: <span class="font-weight-medium">{{ scheduleStore.loanPeriodInMonths }} {{ $i18n.useScheduleModal.MONTHS }}</span></p>
          <p class="mb-1" v-if="!isForeignCurrency(store.userInput) && offer.interestRate.referenceRateType !== ReferenceRateType.OTHER">{{ offer.interestRate.referenceRateType }}: <span class="font-weight-medium">{{ offer.interestRate.referenceRate }} %</span></p>
          <template v-if="offer.features.isSafeCredit2Percent">
            <p class="mb-1" v-html-safe="$i18n.useScheduleModal.TO_120_MONTH_BK_2"></p>
            <p class="mb-1">{{ $i18n.useScheduleModal.AFTER_120_MONTH_BK_2 }}</p>
          </template>
          <template v-if="offer.features.isHomeStartProgram">
            <span class="d-block">Raty do 120 m-ca: z dopłatami (założenia programu Kredyt na start)</span>
            <span class="d-block">Raty po 120 m-cu:</span>
          </template>
          <fp-chip-radio
            v-if="scheduleStore.userInput && isMortgageProduct"
            :items="availableInstallmentTypes"
            item-name="name_pl"
            :mandatory="true"
            @input="onChange()"
            v-model="scheduleStore.userInput.fixedInstallments"
          ></fp-chip-radio>
        </v-col>
      </v-row>
      <ScheduleOptionsAccordion v-if="scheduleStore.schedule" v-model="userInput" @fetchSchedule="fetchSchedule"/>
      <div class="sticky top-0 inset-x-0 z-1">
        <navigation-tabs
          class="px-4 pt-3"
          :tabs-headers="TABS_HEADERS"
          v-model="scheduleStore.scheduleVariant"
          @change="fetchSchedule" />
        <v-divider />
      </div>
      <v-row
        v-if="isScheduleWithEarlyPaymentsHidden"
        class="w-full">
        <v-col class="text-center">
          <p class="headline-4 mt-6 mb-2">
            {{ $i18n.useScheduleModal.NO_EARLY_PAYMENTS }}
          </p>
          <p class="mb-6">{{ $i18n.useScheduleModal.ADD_EARLY_PAYMENT_INFO }}</p>
        </v-col>
      </v-row>
      <schedule-table
        v-else
        :loading="isLoading"
        :offer="offer"
      >
      </schedule-table>
    </v-card-text>
    <v-divider></v-divider>
    <v-flex tag="v-card-actions" px-6 py-4>
      <fp-btn
        primary
        :disabled="isScheduleWithEarlyPaymentsHidden"
        :click="() => printout.downloadSchedulePrintout()">
        <v-icon left class="ml-0">mdi-tray-arrow-down</v-icon>
        <span>{{ $i18n.useScheduleModal.DOWNLOAD_SCHEDULE }}</span>
      </fp-btn>
      <fp-btn
        v-if="$env.isPreprodOrDev"
        :disabled="isScheduleWithEarlyPaymentsHidden"
        :click="() => printout.previewSchedulePrintout()">
        <v-icon left class="ml-md-0">mdi-dev-to</v-icon>
        <span v-if="$vuetify.breakpoint.mdAndUp">{{ $i18n.useScheduleModal.PREVIEW_SCHEDULE }}</span>
      </fp-btn>
      <v-spacer></v-spacer>
      <v-btn outlined depressed @click="modal.closeModal()">{{ $i18n.useScheduleModal.CLOSE }}</v-btn>
    </v-flex>
  </v-card></template>
<script lang="ts" setup>
import {Offer} from '@/models/simulation/Simulation';
import {installmentTypeDictionary} from '@/modules/multiForm/shared/MultiformDictionaries';
import FpChipRadio from '@/components/inputs/FpChipRadio.vue';
import {SimulationDemand} from '@/models/Demand';
import {computed, getCurrentInstance, onMounted, ref} from 'vue';
import {debouncedAtInput} from '@/services/utils/BasicUtils';
import ScheduleTable from '@/components/calculator/schedule/schedule-table/ScheduleTable.vue';
import BankLogo from '@/components/calculator/BankLogo.vue';
import {useModalInstance} from '@/composables/useModalInstance';
import {DebouncedFunc} from 'lodash-es';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import NavigationTabs from '@/components/commons/NavigationTabs.vue';
import {ScheduleVariant} from '@/components/calculator/schedule/models/ScheduleVariant';
import {useScheduleStore} from '@/components/calculator/services/ScheduleStore';
import {usePrintout} from '@/components/calculator/printout/usePrintout';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {IMultiformDictionary} from '@/modules/multiForm/shared/IMultiformDictionary';
import {useSimulationCurrency} from '@/components/calculator/useSimulationCurrency';
import {ReferenceRateType} from '@/models/simulation/ReferenceRateType';
import ScheduleOptionsAccordion from '@/components/calculator/schedule/ScheduleOptionsAccordion.vue';
import {useInstance} from '@/composables/useInstance';
import {ProductType} from '@/commons/enums/ProductType';

type ModalData = {
  offer: Offer,
  userInput: SimulationDemand,
}

const {$errorHandler,} = useInstance();
const modal = useModalInstance<ModalData>();
const store = useSimulationStore();
const printout = usePrintout();
const scheduleStore = useScheduleStore();
const {isForeignCurrency,} = useSimulationCurrency();
const TABS_HEADERS: readonly string[] = [
  I18NGetter().useScheduleModal.WITHOUT_EARLY_PAYMENTS,
  I18NGetter().useScheduleModal.WITH_EARLY_PAYMENTS,
  I18NGetter().useScheduleModal.WITH_EARLY_PAYMENTS_REDUCED_LOAN_PERIOD,
];
const isLoading = ref<boolean>(false);
const offer = ref<Offer>(modal.modalData.offer);
const userInput = ref<SimulationDemand>(modal.modalData.userInput);

const debouncedFetchSchedule = (): DebouncedFunc<() => void> => {
  return debouncedAtInput(async() => {
    await fetchSchedule();
  });
};

const fetchSchedule = async() => {
  isLoading.value = true;
  await scheduleStore.fetchSchedule();
  isLoading.value = false;
};

const onChange: DebouncedFunc<() => void> = debouncedFetchSchedule();

const installmentType = computed<string>(() => {
  return scheduleStore.userInput?.fixedInstallments
    ? I18NGetter().useScheduleModal.FIXED_INSTALLMENT
    : I18NGetter().useScheduleModal.DESCENDING_INSTALLMENT;
});

const isScheduleWithEarlyPaymentsHidden = computed<boolean>(
  () => scheduleStore.scheduleVariant !== ScheduleVariant.WITHOUT_ADDITIONAL_PAYMENT &&
    scheduleStore.earlyPaymentSum === 0
);

const isMortgageProduct = computed(() => store.productType === ProductType.MORTGAGE);

const availableInstallmentTypes = computed<IMultiformDictionary<boolean>>(() => 
  isMortgageProduct.value
    ? (offer.value.restrictions?.onlyFixedInstallments
      ? installmentTypeDictionary.filter((type) => type.type)
      : installmentTypeDictionary)
    : []
);


onMounted(async() => {
  try {
    isLoading.value = true;
    await scheduleStore.initSchedule(offer.value, userInput.value, store.productType!);
  } catch (e) {
    $errorHanlder(e);
  } finally {
    isLoading.value = false;
  }
});
</script>
